import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { getSiteSettings, getCategories } from '@/lib/api';

const LOGO_URL = "/assets/images/logos/main-logo.webp";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState({ email: 'info@sleonline.in', phone: '+91 123 456 7890', address: 'Jaipur, Rajasthan, India' });
  const [productCategories, setProductCategories] = useState([]);

  useEffect(() => {
    getSiteSettings().then(res => setSettings(res.data)).catch(() => {});
    getCategories().then(res => {
      setProductCategories(res.data.map(c => ({ name: c.name, path: `/products?category=${c.id}` })));
    }).catch(() => {});
  }, []);

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Industries', path: '/industries' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Contact', path: '/contact' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
  ];

  return (
    <footer className="bg-primary text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Shubha Lakshmi Enterprises" 
                width="160"
                height="56"
                loading="lazy"
                decoding="async"
                className="h-14 w-auto bg-white rounded-lg p-1"
              />
              <div>
                <span className="font-bold text-white text-lg" style={{ fontFamily: 'Manrope' }}>
                  Shubha Lakshmi
                </span>
                <span className="block text-white/70 text-sm -mt-1">Enterprises</span>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Premium hospitality supplies and eco-friendly packaging solutions. Trusted by hotels, restaurants, and catering services across India.
            </p>
            <div className="flex flex-col gap-3">
              <a href={`mailto:${settings.email}`} className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] text-sm transition-colors">
                <Mail className="w-4 h-4" />
                {settings.email}
              </a>
              <a href={`tel:${settings.phone?.replace(/\s/g, '')}`} className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] text-sm transition-colors">
                <Phone className="w-4 h-4" />
                {settings.phone}
              </a>
              <div className="flex items-start gap-2 text-white/70 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{settings.address}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
              Quick Links
            </h4>
            <ul className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] text-sm transition-colors group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
              Our Products
            </h4>
            <ul className="flex flex-col gap-3">
              {productCategories.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] text-sm transition-colors group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter / CTA */}
          <div>
            <h4 className="font-semibold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
              Work With Us
            </h4>
            <p className="text-white/70 text-sm mb-6">
              Partner with us for all your hospitality supply needs. Quality products, competitive pricing, and reliable delivery.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 rounded-full font-medium text-sm hover:bg-[#B8942D] transition-colors"
              data-testid="footer-contact-btn"
            >
              Request Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © {currentYear} Shubha Lakshmi Enterprises. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-white/50 hover:text-white/70 text-sm transition-colors">
                Privacy Policy
              </Link>
              <span className="text-white/30">|</span>
              <Link to="/admin" className="text-white/50 hover:text-white/70 text-sm transition-colors">
                Admin
              </Link>
              <span className="text-white/30">|</span>
              <a href="https://www.sleonline.in" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white/70 text-sm transition-colors">
                www.sleonline.in
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
